import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useApolloClient,
  NetworkStatus,
  useLazyQuery,
  useMutation
} from '@apollo/client';
import { toast } from 'react-toastify';

import { DownloadIcon, ErrorIcon, UploadSmallIcon } from 'assets/icons';
import { useTreatmentContext } from 'contexts/treatment.context';
import {
  useGetResimFilesFromThreeShape,
  useGetThreeShapeCaseFilesDownloadLink,
  useIsThreeShapeScansUploadCompleted
} from 'services/hooks/ThreeShape.hooks';
import {
  GET_PATIENT_DATA,
  GET_PATIENT_DOCUMENT
} from 'services/query/patientslist';
import { GET_REFERRAL_BY_ID } from 'services/query/referralForm';
import {
  GET_THREE_SHAPE_AUTH_URL,
  GET_THREE_SHAPE_END_SESSION_URL,
  GET_THREE_SHAPE_CASES,
  EXPORT_THREE_SHAPE_SCANS
} from 'services/query/ThreeShape.queries';
import { PatientDetails } from 'types/PatientCard.types';
import { ReferralPatientDetails } from 'types/PatientList.types';
import {
  ThreeShapeCase,
  ThreeShapeDocumentUploadUrl,
  ThreeShapeExportAction,
  ThreeShapeUrlInput
} from 'types/ThreeShape';
import { TreatmentDocumentType } from 'types/TreatmentPlanning.types';
import { translate } from 'utils/locale';
import { showToast } from 'utils/toast/toast.util';

interface UseThreeShapeHooksProps {
  clinicId: string;
  treatmentId?: string;
  referralId?: string;
  pageNumber?: number;
  documentType?: TreatmentDocumentType;
  resubmissionStatus?: string;
  /** When true, `exportThreeShapeScans` sends `getResimFiles: true` and upload uses `getResimFilesFromThreeShape` polling. */
  getResimFiles?: boolean;
  onResimFilesFromThreeShape?: (
    documents: ThreeShapeDocumentUploadUrl[]
  ) => void;
}

const maxPollingAttempts = 50;
const POSITION = 'top-center';
const warningStyle = {
  background: '#FFFFF2',
  border: '1px solid #F0B90F'
};

const scanToastMessage = (title: string, message: string) => {
  return (
    <div className='flex flex-col'>
      <p className='text-DEFAULT_TEXT text-sm font-normal font-sofia-pro'>
        {translate(title)}
      </p>
      <p className='text-DARK_GRAY text-xs font-normal font-sofia-pro'>
        {translate(message)}
      </p>
    </div>
  );
};

const icon = <ErrorIcon className='text-ERROR w-5 h-5' />;

const useThreeShapeHooks = ({
  clinicId,
  treatmentId,
  referralId,
  documentType,
  resubmissionStatus,
  getResimFiles = false,
  onResimFilesFromThreeShape
}: UseThreeShapeHooksProps) => {
  const [isPolling, setIsPolling] = useState(false);
  const { patientDetails } = useTreatmentContext();
  const [isSearchParam, setIsSearchParam] = useState(true);
  const client = useApolloClient();

  const threeShapeInput: ThreeShapeUrlInput = {
    clinicId,
    treatmentId,
    referralId
  };

  const [getAuthUrl, { loading: authLoading }] = useLazyQuery(
    GET_THREE_SHAPE_AUTH_URL
  );

  const [getEndSessionUrl, { loading: endSessionLoading }] = useLazyQuery(
    GET_THREE_SHAPE_END_SESSION_URL
  );

  const [exportThreeShapeScans, { loading: exportLoading }] = useMutation(
    EXPORT_THREE_SHAPE_SCANS
  );

  const [pollingCaseId, setPollingCaseId] = useState<string | null>(null);
  const [activePollingAction, setActivePollingAction] =
    useState<ThreeShapeExportAction | null>(null);
  const pollingAttemptsRef = useRef<number>(0);
  const downloadWaitToastIdRef = useRef<string | number | null>(null);
  const uploadWaitToastIdRef = useRef<string | number | null>(null);
  const hasFetchedRef = useRef(false);
  const searchParams = treatmentId
    ? (patientDetails as PatientDetails)?.name
    : (patientDetails as ReferralPatientDetails)?.name;

  const {
    startPolling: startPollingDownload,
    stopPolling: stopPollingDownload
  } = useGetThreeShapeCaseFilesDownloadLink({
    variables: { caseId: pollingCaseId || '' },
    skip:
      !pollingCaseId ||
      activePollingAction !== ThreeShapeExportAction.DOWNLOAD_SCANS,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (data?.getThreeShapeCaseFilesDownloadLink?.presignedUrl) {
        if (downloadWaitToastIdRef.current !== null) {
          toast.dismiss(downloadWaitToastIdRef.current);
          downloadWaitToastIdRef.current = null;
        }
        stopPollingDownload();
        setIsPolling(false);
        const url = data.getThreeShapeCaseFilesDownloadLink.presignedUrl;
        setPollingCaseId(null);
        setActivePollingAction(null);
        pollingAttemptsRef.current = 0;
        window.open(url, '_blank');
      } else {
        pollingAttemptsRef.current += 1;
        if (pollingAttemptsRef.current >= maxPollingAttempts) {
          if (downloadWaitToastIdRef.current !== null) {
            toast.dismiss(downloadWaitToastIdRef.current);
            downloadWaitToastIdRef.current = null;
          }
          const containerId = 'prescription-toast-container';
          showToast(translate('download.fail'), false, containerId, icon);
          setIsPolling(false);
          stopPollingDownload();
          setPollingCaseId(null);
          setActivePollingAction(null);
          pollingAttemptsRef.current = 0;
        }
      }
    }
  });

  const {
    startPolling: startPollingResimFiles,
    stopPolling: stopPollingResimFiles
  } = useGetResimFilesFromThreeShape({
    variables: {
      caseId: pollingCaseId || '',
      treatmentId: treatmentId || undefined
    },
    skip:
      !pollingCaseId ||
      activePollingAction !== ThreeShapeExportAction.UPLOAD_SCANS ||
      !getResimFiles,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const documents = data?.getResimFilesFromThreeShape;
      if (documents?.length) {
        if (uploadWaitToastIdRef.current !== null) {
          toast.dismiss(uploadWaitToastIdRef.current);
          uploadWaitToastIdRef.current = null;
        }
        stopPollingResimFiles();
        setPollingCaseId(null);
        setActivePollingAction(null);
        setIsPolling(false);
        pollingAttemptsRef.current = 0;
        onResimFilesFromThreeShape?.(documents);
      } else {
        pollingAttemptsRef.current += 1;
        if (pollingAttemptsRef.current >= maxPollingAttempts) {
          if (uploadWaitToastIdRef.current !== null) {
            toast.dismiss(uploadWaitToastIdRef.current);
            uploadWaitToastIdRef.current = null;
          }
          const containerId = 'prescription-toast-container';
          showToast(translate('upload.fail'), false, containerId, icon);
          setIsPolling(false);
          stopPollingResimFiles();
          setPollingCaseId(null);
          setActivePollingAction(null);
          pollingAttemptsRef.current = 0;
        }
      }
    }
  });

  const { startPolling: startPollingUpload, stopPolling: stopPollingUpload } =
    useIsThreeShapeScansUploadCompleted({
      variables: {
        caseId: pollingCaseId || '',
        ...(referralId ? { referralId } : { treatmentId })
      },
      skip:
        !pollingCaseId ||
        activePollingAction !== ThreeShapeExportAction.UPLOAD_SCANS ||
        getResimFiles,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        if (data?.isThreeShapeScansUploadCompleted) {
          if (uploadWaitToastIdRef.current !== null) {
            toast.dismiss(uploadWaitToastIdRef.current);
            uploadWaitToastIdRef.current = null;
          }
          stopPollingUpload();
          setPollingCaseId(null);
          setActivePollingAction(null);
          setIsPolling(false);
          pollingAttemptsRef.current = 0;
          // reupload case
          if (documentType && resubmissionStatus) {
            client.refetchQueries({
              include: [GET_PATIENT_DOCUMENT]
            });
          } else if (treatmentId) {
            client.refetchQueries({
              include: [GET_PATIENT_DATA]
            });
          } else if (referralId) {
            client.refetchQueries({
              include: [GET_REFERRAL_BY_ID]
            });
          }
        } else {
          pollingAttemptsRef.current += 1;
          if (pollingAttemptsRef.current >= maxPollingAttempts) {
            if (uploadWaitToastIdRef.current !== null) {
              toast.dismiss(uploadWaitToastIdRef.current);
              uploadWaitToastIdRef.current = null;
            }
            const containerId = 'prescription-toast-container';
            showToast(translate('upload.fail'), false, containerId, icon);
            setIsPolling(false);
            stopPollingUpload();
            setPollingCaseId(null);
            setActivePollingAction(null);
            pollingAttemptsRef.current = 0;
          }
        }
      }
    });

  useEffect(() => {
    return () => {
      if (downloadWaitToastIdRef.current !== null) {
        toast.dismiss(downloadWaitToastIdRef.current);
      }
      if (uploadWaitToastIdRef.current !== null) {
        toast.dismiss(uploadWaitToastIdRef.current);
      }
      stopPollingDownload();
      stopPollingUpload();
      stopPollingResimFiles();
      setPollingCaseId(null);
      setActivePollingAction(null);
      pollingAttemptsRef.current = 0;
    };
  }, []);

  const [
    getThreeShapeCases,
    { data: casesData, loading: casesLoading, fetchMore, networkStatus }
  ] = useLazyQuery(GET_THREE_SHAPE_CASES, {
    notifyOnNetworkStatusChange: true,
    onError: () => {
      if (treatmentId) {
        client.refetchQueries({
          include: [GET_PATIENT_DATA]
        });
      }
    }
  });

  const handleThreeShapeAuth = async () => {
    const { data } = await getAuthUrl({
      variables: { input: threeShapeInput }
    });

    if (data?.getThreeShapeAuthUrl?.authUrl) {
      window.open(data.getThreeShapeAuthUrl.authUrl, '_blank');
    }
  };

  const handleEndSession = async (action?: string) => {
    const { data } = await getEndSessionUrl({
      variables: { input: { ...threeShapeInput, action } }
    });

    if (data?.getThreeShapeEndSessionUrl?.endSessionUrl) {
      window.open(data.getThreeShapeEndSessionUrl.endSessionUrl, '_blank');
    }
  };

  const fetchMoreCases = useCallback(() => {
    if (!clinicId || !fetchMore) return;
    let initialFilteredCases: string[] = [];
    if (!casesData?.getThreeShapeCases?.hasNext && isSearchParam) {
      initialFilteredCases =
        casesData?.getThreeShapeCases?.edges?.map(
          (edge: ThreeShapeCase) => edge.id
        ) || [];
      setIsSearchParam(false);
    }

    const currentCases = casesData?.getThreeShapeCases?.edges || [];
    const nextPageNumber = Math.floor(
      (currentCases.length - initialFilteredCases.length) / 10
    );

    return fetchMore({
      variables: {
        input: {
          clinicId,
          pageNumber: nextPageNumber,
          ...(casesData?.getThreeShapeCases?.hasNext &&
            isSearchParam && { searchParams })
        }
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        const filteredFetchMoreResult =
          fetchMoreResult?.getThreeShapeCases?.edges?.filter(
            (edge: ThreeShapeCase) => !initialFilteredCases.includes(edge.id)
          );
        if (!fetchMoreResult) return prev;
        return Object.assign({}, prev, {
          getThreeShapeCases: {
            ...(fetchMoreResult?.getThreeShapeCases || {}),
            edges: [
              ...(prev?.getThreeShapeCases?.edges || []),
              ...(filteredFetchMoreResult || [])
            ]
          }
        });
      }
    });
  }, [clinicId, fetchMore, casesData]);

  const handleExportThreeShapeScans = async (
    caseId: string,
    attachments: string[],
    action: ThreeShapeExportAction
  ) => {
    stopPollingDownload();
    stopPollingUpload();
    stopPollingResimFiles();
    setPollingCaseId(null);
    setActivePollingAction(null);
    pollingAttemptsRef.current = 0;

    const isUploadScans = action === ThreeShapeExportAction.UPLOAD_SCANS;
    const { data } = await exportThreeShapeScans({
      variables: {
        input: {
          caseId,
          treatmentId,
          referralId,
          attachments,
          action,
          ...(isUploadScans && documentType ? { documentType } : {}),
          ...(isUploadScans && resubmissionStatus
            ? { resubmissionStatus }
            : {}),
          ...(isUploadScans && getResimFiles ? { getResimFiles: true } : {})
        }
      }
    });

    const response = data?.exportThreeShapeScans;

    if (!response) {
      return;
    }

    if (response.presignedUrl) {
      window.open(response.presignedUrl, '_blank');
      return;
    }

    if (
      response.isTriggered &&
      action === ThreeShapeExportAction.DOWNLOAD_SCANS
    ) {
      pollingAttemptsRef.current = 0;
      setActivePollingAction(ThreeShapeExportAction.DOWNLOAD_SCANS);
      setPollingCaseId(caseId);
      const containerId = 'prescription-toast-container';
      downloadWaitToastIdRef.current = toast.warning(
        scanToastMessage('three.shape.scan.download', 'wait.message'),
        {
          containerId,
          position: POSITION,
          autoClose: false,
          hideProgressBar: true,
          closeButton: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          style: warningStyle,
          icon: <DownloadIcon className='text-DARK_YELLOW' />
        }
      );
      setIsPolling(true);
      startPollingDownload(5000);
    }
    if (
      response.isTriggered &&
      action === ThreeShapeExportAction.UPLOAD_SCANS
    ) {
      pollingAttemptsRef.current = 0;
      setActivePollingAction(ThreeShapeExportAction.UPLOAD_SCANS);
      setPollingCaseId(caseId);
      const containerId = 'prescription-toast-container';
      uploadWaitToastIdRef.current = toast.warning(
        scanToastMessage('three.shape.scan.upload', 'wait.message.short'),
        {
          containerId,
          position: POSITION,
          autoClose: false,
          hideProgressBar: true,
          closeButton: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          style: warningStyle,
          icon: <UploadSmallIcon className='text-DARK_YELLOW' />
        }
      );
      setIsPolling(true);
      if (getResimFiles) {
        startPollingResimFiles(5000);
      } else {
        startPollingUpload(5000);
      }
    }
  };

  const isFetchMoreLoading = networkStatus === NetworkStatus.fetchMore;

  useEffect(() => {
    if (
      !hasFetchedRef.current &&
      clinicId &&
      patientDetails?.isLoggedInToThreeShape
    ) {
      hasFetchedRef.current = true;
      getThreeShapeCases({
        variables: {
          input: {
            clinicId,
            pageNumber: 0,
            searchParams
          }
        },
        ...(getResimFiles
          ? {
              fetchPolicy: 'network-only' as const,
              nextFetchPolicy: 'cache-first' as const
            }
          : {})
      });
    }
  }, [
    getThreeShapeCases,
    clinicId,
    getResimFiles,
    patientDetails?.isLoggedInToThreeShape,
    searchParams
  ]);

  return {
    authLoading,
    endSessionLoading,
    casesLoading: casesLoading || isFetchMoreLoading,
    isLoading:
      authLoading ||
      endSessionLoading ||
      casesLoading ||
      exportLoading ||
      isPolling,
    casesData: casesData?.getThreeShapeCases,
    threeShapeCases: casesData?.getThreeShapeCases?.edges || [],
    hasNext: casesData?.getThreeShapeCases?.hasNext || isSearchParam,
    totalPages: Math.ceil(casesData?.getThreeShapeCases?.totalCount / 10),
    handleThreeShapeAuth,
    handleEndSession,
    fetchMoreCases,
    handleExportThreeShapeScans
  };
};

export default useThreeShapeHooks;
